import { type ElementWriteOptions, groupActionsService } from '@/api/services/group-actions.service'
import type {
  GroupCommandPayload,
  RepositionCommandPayload,
  RestructureCommandPayload,
  UngroupCommandPayload,
} from '@/api/types/group-commands'
import { logger } from '@/utils/logger'

export type { ElementWriteOptions as FlushOptions } from '@/api/services/group-actions.service'

export type QueuedGroupAction = (
  | { kind: 'group'; payload: GroupCommandPayload; seq: number }
  | { kind: 'ungroup'; payload: UngroupCommandPayload; seq: number }
  | { kind: 'restructure'; payload: RestructureCommandPayload; seq: number }
  | { kind: 'reposition'; payload: RepositionCommandPayload; seq: number }
) & {
  /**
   * Asked right before the request would be sent; false drops it. For a group creation that an Undo
   * has since called off (see `pending-group-persist`): by then the group and its children are gone,
   * and sending it would create an empty group.
   */
  shouldSend?: () => boolean
}

export interface GroupActionQueueOptions {
  debounceMs?: number
  maxWaitMs?: number
  onFlushed?: (change: QueuedGroupAction) => void
  onError?: (change: QueuedGroupAction, error: unknown) => void
}

let globalGroupSeq = 0

export class GroupActionQueue {
  private pending: QueuedGroupAction[] = []
  private debounceTimer: ReturnType<typeof setTimeout> | null = null
  private maxWaitTimer: ReturnType<typeof setTimeout> | null = null
  /** When debounceMs is 0, coalesce to one microtask so sync bursts enqueue once before flush. */
  private microFlushScheduled = false
  private readonly debounceMs: number
  private readonly maxWaitMs: number
  private readonly onFlushed?: (change: QueuedGroupAction) => void
  private readonly onError?: (change: QueuedGroupAction, error: unknown) => void
  /** Flushes run one after another, so a request is never overtaken by a later flush's. */
  private flushChain: Promise<void> = Promise.resolve()
  /** Flushes that have been asked for and have not finished. */
  private activeFlushes = 0

  constructor(options?: GroupActionQueueOptions) {
    this.debounceMs = options?.debounceMs ?? 300
    this.maxWaitMs = options?.maxWaitMs ?? 2000
    this.onFlushed = options?.onFlushed
    this.onError = options?.onError
  }

  enqueue(change: Omit<QueuedGroupAction, 'seq'>): void {
    const seq = ++globalGroupSeq

    this.pending.push({ ...change, seq } as QueuedGroupAction)

    if (this.debounceMs <= 0) {
      this.clearTimers()
      if (!this.microFlushScheduled) {
        this.microFlushScheduled = true
        queueMicrotask(() => {
          this.microFlushScheduled = false
          void this.flush()
        })
      }

      return
    }

    this.scheduleFlush()
  }

  /**
   * True while a request is queued or on its way. The change queue's deletes wait for this to clear
   * before they undo what a group request creates: they travel separately, so a delete could
   * otherwise land before the group it removes.
   */
  get isBusy(): boolean {
    return this.pending.length > 0 || this.activeFlushes > 0
  }

  /**
   * Waits for a flush already under way, then sends what is queued. A keepalive flush (tab close)
   * does not wait: its writes must not sit behind requests the browser may be abandoning.
   */
  flush(opts?: ElementWriteOptions): Promise<void> {
    this.clearTimers()

    if (opts?.keepalive) return this.flushPending(opts)

    // With nothing under way it starts at once, not a tick later: a group request enqueued just
    // before a reload must still be on the wire (see the microtask flush in `enqueue`).
    const run =
      this.activeFlushes === 0
        ? this.flushPending(opts)
        : this.flushChain.then(() => this.flushPending(opts))

    this.activeFlushes += 1

    // `send` reports its own errors, but the chain must outlive a flush that rejects anyway.
    this.flushChain = run.then(
      () => undefined,
      () => undefined
    )

    return run.finally(() => {
      this.activeFlushes -= 1
    })
  }

  private async flushPending(opts?: ElementWriteOptions): Promise<void> {
    while (this.pending.length > 0) {
      const snapshot = [...this.pending].sort((a, b) => a.seq - b.seq)

      this.pending = []

      for (const change of snapshot) {
        if (change.shouldSend && !change.shouldSend()) continue
        await this.send(change, opts)
      }
    }
  }

  dispose(): void {
    this.clearTimers()
    this.pending = []
  }

  private scheduleFlush(): void {
    if (this.debounceTimer != null) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => void this.flush(), this.debounceMs)

    if (this.maxWaitTimer == null) {
      this.maxWaitTimer = setTimeout(() => {
        this.maxWaitTimer = null
        void this.flush()
      }, this.maxWaitMs)
    }
  }

  private clearTimers(): void {
    if (this.debounceTimer != null) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }

    if (this.maxWaitTimer != null) {
      clearTimeout(this.maxWaitTimer)
      this.maxWaitTimer = null
    }
  }

  private async send(change: QueuedGroupAction, opts?: ElementWriteOptions): Promise<void> {
    try {
      switch (change.kind) {
        case 'group':
          await groupActionsService.group(change.payload, opts)
          break
        case 'ungroup':
          await groupActionsService.ungroup(change.payload, opts)
          break
        case 'restructure':
          await groupActionsService.restructure(change.payload, opts)
          break
        case 'reposition':
          await groupActionsService.reposition(change.payload, opts)
          break
      }

      this.onFlushed?.(change)
    } catch (error) {
      logger.warn(`GroupActionQueue: failed to send ${change.kind}`, error)
      this.onError?.(change, error)
    }
  }
}
